import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppContext } from "../../AppProvider";
import { useHandlerContext } from "./providers/Handler";
import { faArrowLeft, faLoader } from "../../utilities/fontawesomeIcons";
import { Input, Modal, ModalBody, ModalHeader } from "reactstrap";
import { Link } from "react-router-dom";
import { Company } from "../../Models/Company";
import InputIcon from "../../Components/Inputs/InputIcon/InputIcon";
import { getCompanyPath } from "./utilities";
import { getFieldError } from "../../utilities/utilities";
import CollectionSettings from "./CollectionSettings";
import { createModalConfig, ModalConfig } from "../../Models/ModalConfig";
import { useCallback } from "react";
import NoAvailableImage from "../../assets/images/no-image-available.png";

export const HandlerCompany = () => {
  const { dictionary, owner, accessTokenPayload } = useAppContext();
  const {
    data,
    action,
    error,
    loader,
    edit,
    type,
    modalConfig,
    setModalConfig,
  } = useHandlerContext();

  const titles: Record<string, string> = {
    create: dictionary.words.new_sm,
    clone: dictionary.actions.duplicate,
    edit: dictionary.actions.edit,
    details: dictionary.actions.details,
  };

  const title = {
    CUSTOMER: dictionary.registries.customer,
    AFILIATE: dictionary.registries.affiliate,
    ORGANIZATION: dictionary.registries.organization,
    MASTER_ORGANIZATION: `Master ${dictionary.registries.organization}`,
  };

  const closeModal = () => {
    setModalConfig(createModalConfig());
  };

  const handleChange = (
    name: string,
    value: string | boolean | number | object | null,
    key: string
  ) => {
    edit(
      {
        ...data,
        [name]: value,
      } as Company,
      key as keyof Company
    );
  };

  const openModal = useCallback(
    (
      action: ModalConfig["action"],
      data?: string[],
      size: ModalConfig["size"] = "sm",
      className = "",
      title = ""
    ) => {
      setModalConfig({
        isOpen: true,
        action,
        size,
        data,
        className,
        title,
      });
    },
    [setModalConfig]
  );

  return (
    <>
      <div className="row align-items-center mb-2">
        <div className="col">
          <h2 className="m-0">
            {titles[action]} {title[type]}
          </h2>
        </div>
      </div>

      {!loader ? (
        <>
          <div
            className="d-grid grid-column mt-1"
            style={{ gridTemplateColumns: `1fr 2fr` }}
          >
            <div className="box-container h-100">
              <div
                className="d-grid grid-rows justify-content-center h-100"
                style={{ gridTemplateColumns: `1fr` }}
              >
                <div className="d-flex justify-content-center">
                  <div>
                    <img
                      style={{
                        maxWidth: "300px",
                        maxHeight: "300px",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        display: "block",
                      }}
                      src={data.logoDarkUrl || NoAvailableImage}
                      alt={data.logoDarkUrl ? `${data.name} logo` : ""}
                    />
                  </div>
                </div>
                <div>
                  <label className="label-2">
                    {dictionary.registries.logo_url}
                  </label>
                  <InputIcon
                    type="text"
                    value={data.logoDarkUrl || ""}
                    name="logoDarkUrl"
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "logoDarkUrl");
                    }}
                    error={getFieldError(error, "logoDarkUrl")}
                  />
                </div>
              </div>
            </div>

            <div className="box-container h-100">
              <div
                className="d-grid grid-column mb-2"
                style={{ gridTemplateColumns: `0.5fr` }}
              >
                <div>
                  <label className="label-2">
                    {dictionary.registries.company}
                  </label>
                  <h3>{owner?.name}</h3>
                </div>
              </div>

              <Input
                type="hidden"
                name="type"
                className="form-control"
                value={type}
              />

              <div
                className="d-grid grid-column mb-2"
                style={{ gridTemplateColumns: `1fr 1fr` }}
              >
                <div>
                  <label className="label-2">
                    {dictionary.registries.company_name}
                  </label>
                  <InputIcon
                    type="text"
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "name");
                    }}
                    value={data.name || ""}
                    name="name"
                    error={getFieldError(error, "name")}
                  />
                </div>
                <div>
                  <label className="label-2">
                    {dictionary.registries.commercial_name}
                  </label>
                  <InputIcon
                    type="text"
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "friendlyName");
                    }}
                    value={data.friendlyName || ""}
                    name="friendlyName"
                    error={getFieldError(error, "friendlyName")}
                  />
                </div>
              </div>
              <div
                className="d-grid grid-column mb-2"
                style={{ gridTemplateColumns: `1fr 1fr` }}
              >
                <div>
                  <label className="label-2">
                    {dictionary.registries.vat_number}
                  </label>
                  <InputIcon
                    type="text"
                    name="vatNumber"
                    value={data.vatNumber}
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "vatNumber");
                    }}
                    error={getFieldError(error, "vatNumber")}
                  />
                </div>
                <div>
                  <label className="label-2">
                    {dictionary.references.external_reference}
                  </label>
                  <InputIcon
                    type="text"
                    value={data.externalReference || ""}
                    name="externalReference"
                    onChange={({ target }) => {
                      handleChange(
                        target.name,
                        target.value,
                        "externalReference"
                      );
                    }}
                    error={getFieldError(error, "externalReference")}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col">
              <Link
                to={`/${getCompanyPath(type)}`}
                className="button-1 button-1-transparent"
              >
                <FontAwesomeIcon icon={faArrowLeft} /> {dictionary.words.back}
              </Link>
            </div>
            {/* {type === 'CUSTOMER' && (
                            <div className="col d-flex justify-content-end">
                                <button
                                    title={dictionary.settings.collection_settings}
                                    className="button-1 button-1-transparent"
                                    onClick={() => {
                                        console.log(data.id);
                                        openModal(
                                            "collectionSettings",
                                            [String(data.id)],
                                            "md",
                                            "",
                                            dictionary.settings.collection_settings,
                                        );
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCalendarCheck} /> {dictionary.settings.collection_settings}
                                </button>
                            </div>
                        )} */}
          </div>
        </>
      ) : (
        <div className="text-center mt-2">
          <FontAwesomeIcon icon={faLoader} spin size="2x" />
        </div>
      )}

      <Modal isOpen={modalConfig.isOpen} size={modalConfig.size}>
        <ModalHeader toggle={closeModal}>
          <span className="modal-title">
            <i className={modalConfig.className + " me-2"}></i>
            {modalConfig.title}
          </span>
        </ModalHeader>
        <ModalBody className={modalConfig.className || ""}>
          {modalConfig.action === "collectionSettings" && (
            <CollectionSettings
              userOwnerId={accessTokenPayload.owner}
              companyId={modalConfig.data}
              modalToogle={closeModal}
              refreshTable={false}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
};
